import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import type { TransactionAttachmentEntity } from '@actual-app/core/types/models';

type AttachmentPreviewModalProps = {
  attachment: TransactionAttachmentEntity;
  onClose: () => void;
};

export function AttachmentPreviewModal({
  attachment,
  onClose,
}: AttachmentPreviewModalProps) {
  const { t } = useTranslation();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let disposed = false;
    let objectUrl: string | null = null;
    setPreviewUrl(null);
    setError(null);

    void send('transaction-attachment-read', { id: attachment.id })
      .then(result => {
        if (disposed) return;
        objectUrl = URL.createObjectURL(
          new Blob([result.contents.slice().buffer], {
            type: result.attachment.mime_type,
          }),
        );
        setPreviewUrl(objectUrl);
      })
      .catch(cause => {
        if (!disposed) {
          setError(
            cause instanceof Error
              ? cause.message
              : t('The attachment could not be opened.'),
          );
        }
      });

    return () => {
      disposed = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment, retryKey, t]);

  return (
    <View
      role="dialog"
      aria-modal="true"
      aria-label={t('Preview {{filename}}', {
        filename: attachment.original_filename,
      })}
      style={{
        backgroundColor: theme.pageBackground,
        border: `1px solid ${theme.tableBorder}`,
        borderRadius: 8,
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
        inset: '5vh 5vw',
        padding: 16,
        position: 'fixed',
        zIndex: 10000,
      }}
    >
      <View style={{ alignItems: 'center', flexDirection: 'row', gap: 8 }}>
        <Text style={{ flex: 1, fontWeight: 600 }}>
          {attachment.original_filename}
        </Text>
        {previewUrl && (
          <Button onPress={() => window.open(previewUrl, '_blank', 'noopener')}>
            <Trans>Open in new window</Trans>
          </Button>
        )}
        <Button onPress={onClose}>
          <Trans>Close preview</Trans>
        </Button>
      </View>

      {error ? (
        <View
          role="alert"
          style={{ alignItems: 'flex-start', gap: 8, marginTop: 16 }}
        >
          <Text>{error}</Text>
          <Button onPress={() => setRetryKey(value => value + 1)}>
            <Trans>Try again</Trans>
          </Button>
        </View>
      ) : previewUrl == null ? (
        <Text style={{ marginTop: 16 }}>
          <Trans>Loading preview...</Trans>
        </Text>
      ) : attachment.mime_type === 'application/pdf' ? (
        <iframe
          title={t('Preview {{filename}}', {
            filename: attachment.original_filename,
          })}
          src={previewUrl}
          style={{
            border: 0,
            height: 'calc(90vh - 72px)',
            marginTop: 12,
            width: '100%',
          }}
        />
      ) : (
        <img
          src={previewUrl}
          alt={attachment.original_filename}
          style={{
            display: 'block',
            height: 'calc(90vh - 72px)',
            margin: '12px auto 0',
            maxWidth: '100%',
            objectFit: 'contain',
          }}
        />
      )}
    </View>
  );
}
